import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { DetailNavType } from '../../closer-sync-mortgage/closer-sync-mortgage.component';
import { CloserSyncMortgageSelectors } from '../../closer-sync-mortgage/closer-sync-mortgage.selector';
import {
	PACKAGE_STATUS_TYPES,
	PackageStatus,
} from '../../closer-sync-mortgage/closer-sync-mortgage.model';
import { PackageType } from 'src/app/shared/api-repos/mortgages/closer-sync-instructions.api.service';
import { STATUSES } from '@models/statuses';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import {
	FundingType,
	FundsReceived,
	MatterMortgage,
	MatterMortgageType,
	MatterMortgageWithAssociations,
	SYNC_FLOW_TYPES,
	TransactionType,
} from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessUser } from '@models/businesses';
import { BaseComponent } from '@core/base.component';
import { Router } from '@angular/router';
import { FormTypes } from '@models/global-forms';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { GlobalFormsRepo } from '@repos/global-forms/global-forms.repo';
import { Lender } from '@models/lender';
import { getMortgagePrimaryLender } from '@core/util/mortgage';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { clientLegalName } from '@core/util/client';

interface VM {
	rffPackageStatus?: PackageStatus;
	frPackageStatus?: PackageStatus;
	mortgage?: MatterMortgage;
	_closeDate: Date;
	fundsReceived?: FundsReceived;
	syncFlowType?: SYNC_FLOW_TYPES;
	rffTransmissionLink?: string;
	rffTransmissionType?: string;
	rffTransmissionTypePastTense?: string;
	rffTransmissionText?: string;
	frTransmissionLink?: string;
	frTransmissionType?: string;
	frTransmissionTypePastTense?: string;
	frTransmissionText?: string;
	hasForms?: boolean;
	primaryLender?: Lender;
}

const defaultVM: VM = {
	rffPackageStatus: null,
	frPackageStatus: null,
	_closeDate: null,
	mortgage: null,
	fundsReceived: null,
	syncFlowType: SYNC_FLOW_TYPES.FAX,
	rffTransmissionLink: null,
	rffTransmissionType: null,
	rffTransmissionTypePastTense: null,
	rffTransmissionText: null,
	frTransmissionLink: null,
	frTransmissionType: null,
	frTransmissionTypePastTense: null,
	frTransmissionText: null,
	hasForms: false,
	primaryLender: null,
};

@Component({
	selector: 'app-closer-sync-prompts',
	templateUrl: './closer-sync-prompts.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncPromptsComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy
{
	@Input() syncFlowType: SYNC_FLOW_TYPES;
	@Output() gotoArea = new EventEmitter<string>();

	public DetailNavType = DetailNavType;
	public PACKAGE_STATUS_TYPES = PACKAGE_STATUS_TYPES;
	public STATUSES = STATUSES;
	public SYNC_FLOW_TYPES = SYNC_FLOW_TYPES;
	public PackageType = PackageType;
	public FORM_TYPES = FormTypes;
	public TransactionType = TransactionType;
	public fundingRequired = true;

	@SelectSnapshot(BusinessUsersState.getLoggedInBusinessUser)
	user: BusinessUser;
	@Select(MatterMortgagesState.getSelectedMortgage)
	mortgage$: Observable<MatterMortgageWithAssociations>;
	@Select(CloserSyncMortgageSelectors.getRequestForFundsPackageStatus)
	requestForFundsPackageStatus$: Observable<PackageStatus>;
	@Select(CloserSyncMortgageSelectors.getFinalReportPackageStatus)
	finalReportPackageStatus$: Observable<PackageStatus>;
	@SelectSnapshot(SelectedMatter.matterNumber) matterNumber: string;
	@SelectSnapshot(MatterClients.get_clients) clients: Clients[];
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;
	private rffSub;
	private frSub;
	private mortgageSub;

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private router: Router,
		private analytics: AnalyticsService,
		private globalMortgageFormsRepo: GlobalFormsRepo,
	) {
		super(defaultVM);
	}

	async ngOnInit(): Promise<void> {
		this.mortgageSub = this.mortgage$.subscribe(async (mortgage) => {
			this.VM = {
				mortgage,
			};
			if (mortgage?.fundsReceived) {
				this.VM = {
					fundsReceived: mortgage.fundsReceived,
				};
			}

			if (mortgage?.fundingDetails?.type === FundingType.NO_FUNDING_REQUIRED) {
				this.fundingRequired = false;
			} else {
				this.fundingRequired = true;
			}

			const lender = getMortgagePrimaryLender(mortgage);
			this.VM = {
				primaryLender: lender,
			};

			let rffTransmissionType = lender?.requestForFundsContact?.type ?? 'send';
			if (mortgage?.rffEmail || mortgage?.lenderContactEmail) {
				rffTransmissionType = 'email';
			}
			if (mortgage?.rffFaxNumber) {
				rffTransmissionType = 'fax';
			}

			const rffTransmissionTypePastTense =
				rffTransmissionType === 'send' ? 'sent' : rffTransmissionType + 'ed';

			let frTransmissionType = lender?.finalReportContact?.type ?? 'send';
			if (mortgage?.finalReportEmail) {
				frTransmissionType = 'email';
			}
			if (mortgage?.finalReportFaxNumber) {
				frTransmissionType = 'fax';
			}

			const frTransmissionTypePastTense =
				frTransmissionType === 'send' ? 'sent' : frTransmissionType + 'ed';

			this.VM = {
				syncFlowType:
					this.syncFlowType ?? mortgage?.syncFlowType ?? SYNC_FLOW_TYPES.FAX,
				rffTransmissionType,
				rffTransmissionTypePastTense,
				rffTransmissionText:
					(mortgage?.rffFaxNumber ||
						(lender?.requestForFundsContact?.isProvidedViaInstructions
							? mortgage?.lenderContactEmail || 'the Mortgage Funder'
							: mortgage?.rffEmail ||
								  lender?.requestForFundsContact?.type === 'email'
								? lender?.requestForFundsContact?.email
								: lender?.requestForFundsContact?.fax)) ??
					lender?.displayName ??
					lender?.name ??
					'the lender',
				frTransmissionType,
				frTransmissionTypePastTense,
				frTransmissionText:
					(mortgage?.finalReportFaxNumber ||
						mortgage?.finalReportEmail ||
						(lender?.finalReportContact?.type === 'email'
							? lender?.finalReportContact?.email
							: lender?.finalReportContact?.fax)) ??
					lender?.displayName ??
					lender?.name ??
					'the lender',
			};

			const clientLegalNames = this.clients
				.map((c) => clientLegalName(c, this.nameFormattingSettings))
				.join(', ');

			this.VM = {
				rffTransmissionLink: `mailto:${this.VM.rffTransmissionText}?subject=RE: ${lender?.shortName ?? ''} File #${this.matterNumber}, ${clientLegalNames} &body=Please find the attached Request for Funds package for your review and approval.`,
				frTransmissionLink: `mailto:${this.VM.frTransmissionText}?subject=RE: ${lender?.shortName ?? ''} File #${this.matterNumber}, ${clientLegalNames} &body=Please find the attached Final Report package for your review and approval.`,
			};

			const hasForms =
				mortgage.type === MatterMortgageType.CLOSER_SYNC ||
				(
					await this.globalMortgageFormsRepo.getMortgageFormsByLenderChargeType(
						lender?.id,
						mortgage?.chargeType,
					)
				)?.length > 0;

			this.VM = {
				hasForms,
			};
		});
		this.rffSub = this.requestForFundsPackageStatus$.subscribe(
			(packageStatus) => {
				this.VM = {
					rffPackageStatus: packageStatus,
				};
			},
		);
		this.frSub = this.finalReportPackageStatus$.subscribe((packageStatus) => {
			this.VM = {
				frPackageStatus: packageStatus,
			};
		});
	}

	onRffSentChange(value: boolean) {
		if (value) {
			this.mortgageDispatch.updateMortgage(this.VM.mortgage?.id, {
				fundsReceived: {
					date: Date.now(),
					confirmedBy: {
						id: this.user.id,
						name: this.user.firstName + ' ' + this.user.lastName,
					},
				} as FundsReceived,
			});
		} else {
			this.mortgageDispatch.updateMortgage(this.VM.mortgage?.id, {
				fundsReceived: null,
			});
		}
	}

	onRffEmailSentChange(value: boolean) {
		if (value) {
			this.mortgageDispatch.updateMortgage(this.VM.mortgage?.id, {
				isRffEmailSent: {
					date: Date.now(),
					confirmedBy: {
						id: this.user.id,
						name: `${this.user.firstName} ${this.user.lastName}`,
					},
				} as FundsReceived,
			});
		} else {
			this.mortgageDispatch.updateMortgage(this.VM.mortgage?.id, {
				isRffEmailSent: null,
			});
		}
	}

	onFrEmailSentChange(value: boolean) {
		if (value) {
			this.mortgageDispatch.updateMortgage(this.VM.mortgage?.id, {
				isFrEmailSent: {
					date: Date.now(),
					confirmedBy: {
						id: this.user.id,
						name: `${this.user.firstName} ${this.user.lastName}`,
					},
				} as FundsReceived,
			});
		} else {
			this.mortgageDispatch.updateMortgage(this.VM.mortgage?.id, {
				isFrEmailSent: null,
			});
		}
	}

	ngOnDestroy(): void {
		this.rffSub.unsubscribe();
		this.frSub.unsubscribe();
		this.mortgageSub.unsubscribe();
	}

	gotoRff(event: Event, logEvent?: boolean) {
		if (logEvent) {
			this.analytics.logEvent(AnalyticsEvent.SYNC_MORTGAGE_PAGE_RESEND);
		}
		this.goto(
			event,
			DetailNavType.DOCUMENTS,
			'send-to-lender',
			FormTypes.REQUEST_FOR_FUNDS,
		);
	}

	gotoFinalReport(event: Event) {
		this.goto(
			event,
			DetailNavType.DOCUMENTS,
			'send-to-lender',
			FormTypes.FINAL_REPORT,
		);
	}

	gotoHistory(event: Event) {
		this.goto(event, DetailNavType.DOCUMENTS, 'history');
	}

	goto(event: Event, path: string, tab?: string, selectedForm?: string) {
		this.gotoArea.emit(path);

		if (selectedForm) {
			this.router.navigate([], {
				queryParams: { selectedForm },
				queryParamsHandling: 'merge',
			});
		}

		if (tab) {
			this.router.navigate([], {
				queryParams: { tab },
				queryParamsHandling: 'merge',
			});
		}
	}
}
